import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import dotsIcon from 'assets/images/icons/table-icons.svg';
import ConfirmationModal from 'components/common-components/ConfirmationModal';
import MultiLevelSelect from 'components/form-components/MultiLevelSelect';
import { CallAPI } from 'actions/General';

const InternalDashboardPageActions = ({ ids, actionsCallBack }) => {
  const { t } = useTranslation();
  const { dashboardsList, pageActionsCallBack } = actionsCallBack;

  const [showConfirmType, setShowConfirmType] = useState(null);
  const [moveToDashboardId, setMoveToDashboardId] = useState(null);
  const [actionsOptions, setActionsOptions] = useState({ children: [] });

  const deletePage = async () => {
    const result = await CallAPI('DELETE_PAGE', null, ids[0].id);
    if (result?.status) {
      toast.success(t('alert_success_dashboard_page_deleted'));
      pageActionsCallBack('refresh');
    } else {
      toast.error(result.error);
    }
    setShowConfirmType(null);
  };

  const duplicatePage = async () => {
    const result = await CallAPI('DUPLICATE_PAGE', null, ids[0].id);
    if (result?.status) {
      toast.success(t('alert_success_dashboard_page_duplicated'));
      pageActionsCallBack('refresh');
    } else {
      toast.error(result.error);
    }
    setShowConfirmType(null);
  };

  const movePageToOtherDashboard = async () => {
    const result = await CallAPI('MOVE_PAGE_TO_OTHER_DASHBOARD', {
      pageId: ids[0].id,
      dashboardId: moveToDashboardId,
      position: -1,
    });
    if (result?.status) {
      toast.success(t('alert_success_dashboard_page_moved'));
      pageActionsCallBack('refresh');
    } else {
      toast.error(result.error);
    }
    setShowConfirmType(null);
    setMoveToDashboardId(null);
  };

  const handleAction = (name, value, path) => {
    if (name === 'pageAction') {
      if (value === 'edit') pageActionsCallBack('edit', { id: ids[0].id });
      else if (value === 'delete' || value === 'duplicate') setShowConfirmType(value);
      else if (path === 'moveTo') {
        setShowConfirmType(path);
        setMoveToDashboardId(value);
      }
    }
  };

  useEffect(() => {
    const allOptions = [
      { value: 'edit', displayValue: 'select_edit', permission: true },
      { value: 'duplicate', displayValue: 'select_duplicate', permission: true },
      { value: 'delete', displayValue: 'select_delete', permission: true },
      {
        value: 'moveTo',
        displayValue: 'select_move_to',
        permission: true,
        children: dashboardsList.map((d) => ({ value: d._id, displayValue: d.dashboardTitle })),
      },
    ];

    const visibleOptions = allOptions.filter((option) => option.permission);

    setActionsOptions({ children: visibleOptions });
  }, [ids.length]);

  return (
    <>
      <MultiLevelSelect
        options={actionsOptions}
        value={[]}
        name='pageAction'
        handleChange={(name, value, displayValue, path) => handleAction(name, value[0], path[0])}
        classes='theme-font-inter-regular'
        toggleClasses=''
        toggleComponent={<img src={dotsIcon} className='cursor-pointer' />}
      />
      {!!showConfirmType && (
        <ConfirmationModal
          show={!!showConfirmType}
          title={t(
            showConfirmType === 'delete'
              ? 'alert_dashboard_page_deletion_title'
              : showConfirmType === 'duplicate'
              ? 'alert_dashboard_page_duplication_title'
              : 'alert_dashboard_page_move_title',
          )}
          message={t(
            showConfirmType === 'delete'
              ? 'alert_dashboard_page_deletion_message'
              : showConfirmType === 'duplicate'
              ? 'alert_dashboard_page_duplication_message'
              : 'alert_dashboard_page_move_message',
          )}
          actionList={[
            { color: 'black-1', text: t('button_cancel'), onClick: () => setShowConfirmType(null) },
            showConfirmType === 'delete'
              ? { color: 'red-1', text: t('button_delete'), onClick: () => deletePage() }
              : showConfirmType === 'duplicate'
              ? { color: 'black-1', text: t('button_duplicate'), onClick: () => duplicatePage() }
              : {
                  color: 'black-1',
                  text: t('button_move_to'),
                  onClick: () => movePageToOtherDashboard(),
                },
          ]}
        />
      )}
    </>
  );
};
InternalDashboardPageActions.propTypes = {
  actionData: PropTypes.object.isRequired,
  row: PropTypes.object.isRequired,
  ids: PropTypes.array.isRequired,
  isActionButton: PropTypes.bool.isRequired,
  actionsCallBack: PropTypes.func.isRequired,
};
export default InternalDashboardPageActions;
